<script lang="ts" setup>
import type { FieldFilter, FilterRelationalQuantifier } from '#layers/data-model';
import type { PrimitiveFieldPath } from '#layers/data-model/utils/collection-field-path';
import type { SegmentedFieldFilter } from './utils/filter-path';
import type {
	FilterSchemaCollection,
	FilterSchemaPrimitiveField,
} from './utils/filter-schema';
import type { UIFilterOperator } from './utils/filter-ui-operator';
import { createReusableTemplate } from '@vueuse/core';
import { useEmitAsProps } from 'reka-ui';
import { computed } from 'vue';
import { FilterOperator, getFieldFilterOperatorValue } from '#layers/data-model';
import { useFieldTypeTemplate } from '#layers/data-model/composables/use-field-type-template';
import { getNarrowedOperatorsForField, useFilterOperator } from '~/composables/use-filter-operator';
import { buildSegmentsFromFieldIds, isPrimitiveSegment, isRelationalSegment, parseSegmentedFilter, serializeSegmentedFilter } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { modelToUi, UI_NOT_NULL, uiToModel } from './utils/filter-ui-operator';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

const props = withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	includeRelationFields?: boolean;
	hideGroupActions?: boolean;
	disabled?: boolean;
	readonly?: boolean;
}>(), {
	includeRelationFields: true,
});

const emits = defineEmits<{
	remove: [];
	duplicate: [];
	turnIntoGroup: [];
}>();

const filter = defineModel<FieldFilter>({ required: true });
const { operator, getOperatorLabel } = useFilterOperator(filter);
const schema = injectFilterSchemaContext();
const { findPrimitiveFieldById } = schema;
const actionEmits = useEmitAsProps(emits);

const internalFilter = computed({
	get(): SegmentedFieldFilter | null {
		try {
			return parseSegmentedFilter(filter.value, props.collection, schema);
		}
		catch {
			return null;
		}
	},
	set(newFilter: SegmentedFieldFilter | null) {
		if (!newFilter) return;
		filter.value = serializeSegmentedFilter(newFilter, schema);
	},
});

const segments = computed(() => internalFilter.value?.segments ?? []);

function onFieldPathSelect(index: number, fieldPath: PrimitiveFieldPath) {
	if (!internalFilter.value) return;

	const keptSegments = internalFilter.value.segments.slice(0, index);
	const lastKeptSegment = keptSegments.at(-1);
	const startCollection = lastKeptSegment && isRelationalSegment(lastKeptSegment)
		? schema.findOppositeCollectionByRelationFieldId(lastKeptSegment.fieldId) ?? props.collection
		: props.collection;
	const newSegments = buildSegmentsFromFieldIds(fieldPath, startCollection, schema);
	const lastSegment = newSegments.at(-1);

	if (newSegments.length > 0) {
		const firstOperatorOption = lastSegment && isPrimitiveSegment(lastSegment) ? getOperatorOptions(findPrimitiveFieldById(lastSegment.fieldId))?.[0]?.value : undefined;
		const seed = firstOperatorOption ? uiToModel(firstOperatorOption, { operator: undefined, value: undefined }) : { operator: undefined, value: undefined };

		internalFilter.value = {
			segments: [...keptSegments, ...newSegments],
			operator: seed.operator,
			value: seed.value,
		};
	}
}

function onUpdateQuantifier(index: number, quantifier: FilterRelationalQuantifier) {
	if (!internalFilter.value) return;

	const newSegments = [...internalFilter.value.segments];
	const segment = newSegments[index];

	if (segment?.type === 'to-many') {
		newSegments[index] = { ...segment, quantifier };
		internalFilter.value = { ...internalFilter.value, segments: newSegments };
	}
}

const primitiveField = computed(() => {
	const finalSegment = segments.value.at(-1);

	if (finalSegment && isPrimitiveSegment(finalSegment)) {
		return findPrimitiveFieldById(finalSegment.fieldId);
	}

	return null;
});

const { resolveFilterConfig } = useFilterConfigResolver();
const { typeOptions } = useFieldTypeTemplate(computed(() => primitiveField.value ?? null));

const filterConfig = computed(() => resolveFilterConfig(primitiveField.value ?? undefined));

const isInvalidFilter = computed(() => {
	if (internalFilter.value === null) {
		return true;
	}

	const finalSegment = segments.value.at(-1);

	if (!finalSegment) {
		return true;
	}

	if (finalSegment && isPrimitiveSegment(finalSegment) && primitiveField.value == null) {
		return true;
	}

	return false;
});

const operatorOptions = computed<{ label: string; value: UIFilterOperator }[]>(() => getOperatorOptions(primitiveField.value ?? undefined));

const writableOperator = computed<UIFilterOperator | undefined>({
	get: () => modelToUi(operator.value, getFieldFilterOperatorValue(filter.value)),
	set(op) {
		if (op === undefined || !internalFilter.value) return;

		const next = uiToModel(op, { operator: writableOperator.value, value: internalFilter.value.value });
		internalFilter.value = { ...internalFilter.value, ...next };
	},
});

function getOperatorOptions(field?: FilterSchemaPrimitiveField) {
	const config = resolveFilterConfig(field);
	const operators = getNarrowedOperatorsForField(field, config?.supportedOperators);

	return [
		...operators.map((op) => ({
			label: getOperatorLabel(op),
			value: op,
		})),
		// "is null" / "isn't null" for nullable fields — both map to the `_null` operator
		// at the model layer (true / false respectively), see filter-ui-operator.ts.
		...(field?.isNullable
			? [
					{
						label: getOperatorLabel(FilterOperator.NULL),
						value: FilterOperator.NULL,
					},
					{
						label: getOperatorLabel(UI_NOT_NULL),
						value: UI_NOT_NULL,
					},
				] as const
			: []
		),
	];
}

const [DefineFilterError, ReuseFilterError] = createReusableTemplate();
</script>

<template>
	<DefineFilterError>
		<FilterHandleGroup class="border-error">
			<div class="flex items-center gap-1.5 px-2 text-error">
				<DIcon name="lucide:circle-alert" class="size-4" />
				<span class="text-xs">{{ $t('filters.invalid_filter') }}</span>
			</div>
			<FilterRemove v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
	</DefineFilterError>

	<NuxtErrorBoundary>
		<template #error>
			<ReuseFilterError v-if="isInvalidFilter" />
		</template>

		<ReuseFilterError v-if="isInvalidFilter" />

		<FilterHandleGroup v-else>
			<FilterFieldPath
				:segments
				:disabled
				:include-relation-fields
				@update-from-segment="onFieldPathSelect"
				@update-quantifier="onUpdateQuantifier"
			/>

			<DSelectMenu
				v-model="writableOperator"
				:items="operatorOptions"
				value-key="value"
				variant="ghost"
				trailing-icon=""
				class="rounded-none"
				:search-input="operatorOptions.length > 5"
				:ui="{
					base: 'px-2',
					content: 'min-w-fit',
					value: 'text-xs',
				}"
				:disabled
			/>
			<FilterInputGroup
				v-model="internalFilter"
				:primitive-field="primitiveField!"
				:filter-config
				:type-options
				:disabled
				:readonly
				@remove="emits('remove')"
			/>
			<FilterActions v-if="hideGroupActions && !readonly" v-bind="actionEmits" can-turn-into-group :disabled />
			<FilterRemove v-else-if="!readonly" v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
	</NuxtErrorBoundary>
</template>
